import { isProxyRequest, PROXY_PATH_TRIGGER } from '@lemonade-hq/uniclient-chat';
import { isNativeApp, LimeProductLine, nativeApp } from '@lemonade-hq/uniclient-mobile-bridge';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavContextValue } from './NavProvider';
import { useNavContext } from './NavProvider';
import { useUniclientTrackingContext } from './UniclientTrackingProvider';

interface NativeActionOverride {
  readonly action: (url?: string) => Promise<void>;
  readonly actionType?: 'logout' | 'openChat' | 'redirectToHome';
  readonly actionParams?: Record<string, unknown>;
}

function informHostingParentOnRedirect(url: string): void {
  if (typeof window !== 'undefined' && window.parent !== window) {
    window.parent.postMessage(
      {
        type: 'UNICLIENT_URL_CHANGE',
        payload: {
          url,
        },
      },
      '*',
    );
  }
}

function regexActionMap(url: string): NativeActionOverride | undefined {
  const chatRegexActions = [
    {
      regex: /\/car\/quotes\/([\w-]+)\/payment\/1$/,
      action: (match: RegExpExecArray) => ({
        route: 'car/quotes/init_chat',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        routeParams: { identifier: 'car_app', resource_id: match[1], restart: 'true' },
        hideBack: true,
      }),
    },
    {
      regex: /\/onboarding\?p=car$/,
      action: (_match: RegExpExecArray) => ({
        fromLimePage: LimeProductLine.Car,
        route: 'car/quotes/init_chat',
        routeParams: { identifier: 'car_app', restart: 'true' },
      }),
    },
    {
      regex: /\/start\?moving_flow=true&ownership=owner&policy_public_id=([\w-]+)$/,
      action: (_match: RegExpExecArray) => ({
        fromLimePage: LimeProductLine.Homeowners,
        route: 'quotes/init_chat',
        routeParams: { ownership: 'owner', restart: 'true' },
      }),
    },
    {
      regex: /\/checkout-edge[^?]*\?p=car&quotePublicId=([^&]+)$/,
      action: (match: RegExpExecArray) => ({
        route: 'car/quotes/init_chat',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        routeParams: { identifier: 'car_app', resource_id: match[1], restart: 'true' },
        hideBack: true,
      }),
    },
    {
      regex:
        /\/car\/quotes\/init_chat\?identifier=car_app_fixed_price_post_payment&resource_id=([\w-]+)&policy_id=([\w-]+)/,
      action: (match: RegExpExecArray) => ({
        route: 'car/quotes/init_chat',
        routeParams: {
          identifier: 'car_app_fixed_price_post_payment',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          resource_id: match[1],
          // eslint-disable-next-line @typescript-eslint/naming-convention
          policy_id: match[2],
          restart: 'true',
        },
        hideBack: true,
      }),
    },
    {
      regex:
        /target=chat_route\|route=pet\/claims\/init_chat\|additional_params=identifier:pet_claim_v2,resource_id:([\w-]+),restart:(true|false)(,claim_id:([\w-]+)|)$/,
      action: (match: RegExpExecArray) => {
        return {
          fromLimePage: LimeProductLine.Pet,
          route: 'pet/claims/init_chat',
          routeParams: {
            identifier: 'pet_claim_v2',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            resource_id: match[1],
            restart: match[2],
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ...(match[3] && { claim_id: match[4] }),
          },
        };
      },
    },
    {
      regex:
        /\/car\/quotes\/init_chat\?identifier=car_app_endorsements_photo_collection&resource_id=([\w-]+)&policy_id=([\w-]+)&vehicle_id=([\w-]+)/,
      action: (match: RegExpExecArray) => ({
        route: 'car/quotes/init_chat',
        routeParams: {
          identifier: 'car_app_endorsements_photo_collection',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          resource_id: match[1],
          // eslint-disable-next-line @typescript-eslint/naming-convention
          policy_id: match[2],
          // eslint-disable-next-line @typescript-eslint/naming-convention
          vehicle_id: match[3],
        },
      }),
    },
    // Add more regex patterns and corresponding actions here
  ];

  for (const { regex, action } of chatRegexActions) {
    const match = regex.exec(url);
    if (match) {
      const actionParams = action(match);
      return {
        action: async () => {
          await nativeApp.openChat(actionParams);
        },
        actionType: 'openChat',
        actionParams,
      };
    }
  }

  return undefined;
}

function chatActionMap(url: string, nav: NavContextValue['nav']): NativeActionOverride | undefined {
  const chatActions = {
    [`${nav.monolithUrl}/onboarding?p=pet`]: {
      fromLimePage: LimeProductLine.Pet,
      route: 'pet/quotes/init_chat',
      routeParams: { identifier: 'pet', restart: 'true' },
    },
    [`${nav.monolithUrl}/onboarding?p=home_us_owners`]: {
      fromLimePage: LimeProductLine.Homeowners,
      route: 'quotes/init_chat',
      routeParams: { ownership: 'owner', restart: 'true' },
    },
    [`${nav.monolithUrl}/start?moving_flow=true`]: {
      fromLimePage: LimeProductLine.Renters,
      route: 'quotes/init_chat',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      routeParams: { ownership: 'renter', restart: 'true', cs_variant: 'generic' },
    },
    [`${nav.monolithUrl}/onboarding?p=home_us_renters`]: {
      fromLimePage: LimeProductLine.Renters,
      route: 'quotes/init_chat',
      routeParams: { ownership: 'renter', restart: 'true' },
    },
    [`${nav.monolithUrl}/onboarding?p=car`]: {
      fromLimePage: LimeProductLine.Car,
      route: 'car/quotes/init_chat',
      routeParams: { identifier: 'car_app', restart: 'true' },
    },
    [`${nav.monolithUrl}/onboarding?p=home_us`]: {
      fromLimePage: LimeProductLine.Homeowners,
      route: 'quotes/init_chat',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      routeParams: { restart: 'true', cs_variant: 'generic' },
    },
    [`${nav.monolithUrl}/start?moving_flow=true&ownership=owner`]: {
      fromLimePage: LimeProductLine.Homeowners,
      route: 'quotes/init_chat',
      routeParams: { ownership: 'owner', restart: 'true' },
    },
  };

  const actionParams = chatActions[url];

  if ((actionParams as unknown) == null) return undefined;

  return {
    action: async () => {
      await nativeApp.openChat(actionParams);
    },
    actionType: 'openChat',
    actionParams,
  };
}

function navActionMap(
  url: string,
  nav: NavContextValue['nav'],
  auth: NavContextValue['auth'],
): NativeActionOverride | undefined {
  const navActions = {
    [auth.logoutUrl]: {
      action: async () => {
        await nativeApp.logout();
      },
      actionType: 'logout',
    },
    [nav.homeUrl]: {
      action: async () => {
        await nativeApp.redirectToHome({});
      },
      actionType: 'redirectToHome',
    },
    [nav.consumerUrl]: {
      action: async () => {
        await nativeApp.redirectToHome({});
      },
      actionType: 'redirectToHome',
    },
  } as const;

  return navActions[url];
}

function sanitizeUrl(url: string): string {
  return url.replace('.public', '').replace('.us-east-1', '').replace('eu-east-1', '');
}

function sanitizeEnvId(url: string): string {
  return url.replace(/https:\/\/uniclient-([^.]+)/, 'https://uniclient');
}

function sanitizeSubdomain(url: string): string {
  return url.replace(/https:\/\/([^.]+)\./, 'https://');
}

function getUrlPath(url: string): string {
  const existingUrl = new URL(window.location.href);
  const themeParam = existingUrl.searchParams.get('theme');

  const urlObj = new URL(url, window.location.origin);

  if (themeParam !== null && themeParam.trim() !== '') {
    urlObj.searchParams.set('theme', themeParam);
  }

  if (isProxyRequest(window.location.pathname)) {
    return `/${PROXY_PATH_TRIGGER}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
  }

  return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
}

function isUrlInternal(redirectUrl: string): boolean {
  const currentOrigin = sanitizeUrl(window.location.origin);
  const redirectOrigin = new URL(sanitizeUrl(redirectUrl), currentOrigin).origin;

  if (window.lemonade?.stage === 'staging' || window.lemonade?.stage === 'development') {
    return sanitizeEnvId(currentOrigin) === sanitizeEnvId(redirectOrigin);
  }

  if (window.lemonade?.stage === 'production') {
    return sanitizeSubdomain(currentOrigin) === sanitizeSubdomain(redirectOrigin);
  }

  return redirectOrigin === currentOrigin;
}

export function isUrlSameScript(redirectUrl: string): boolean {
  if (!isUrlInternal(redirectUrl)) {
    return false;
  }

  const currentPath = sanitizeUrl(window.location.pathname);
  const redirectPath = new URL(sanitizeUrl(redirectUrl), window.location.origin).pathname;
  return redirectPath === currentPath;
}

export interface RedirectContextValue {
  readonly redirectFn: (url: string, fallback?: (url: string) => void) => void;
  readonly webRedirectFn: (url: string) => void;
  readonly nativeRedirectFn: (url: string) => void;
  readonly isUrlInternal: (url: string) => boolean;
  readonly isUrlSameScript: (url: string) => boolean;
}

type NativeRedirectFn = (url: string, fallback?: (url: string) => void) => void;

const DEFAULT_WEB_REDIRECT_ACTION = (url: string): void => window.location.replace(url);

const DEFAULT_NATIVE_REDIRECT_ACTION: NativeRedirectFn = (url: string): void => {
  void nativeApp.openUrl({ url, externalBrowser: true, inAppBrowser: false, downloadable: false });
};

interface RedirectProviderProps extends PropsWithChildren {
  readonly webRedirectFn?: (url: string) => void;
}

export const RedirectContext = createContext<RedirectContextValue>({
  redirectFn: DEFAULT_WEB_REDIRECT_ACTION,
  webRedirectFn: DEFAULT_WEB_REDIRECT_ACTION,
  nativeRedirectFn: DEFAULT_NATIVE_REDIRECT_ACTION,
  isUrlInternal: isUrlInternal,
  isUrlSameScript: isUrlSameScript,
});

export const RedirectProvider: React.FC<RedirectProviderProps> = ({
  children,
  webRedirectFn = DEFAULT_WEB_REDIRECT_ACTION,
}) => {
  const { nav, auth } = useNavContext();
  const { trackEvent } = useUniclientTrackingContext();

  const navigate = useNavigate();

  const webActionFn = useCallback(
    (redirectUrl: string, fallback: (url: string) => void = webRedirectFn): void => {
      const isInternal = isUrlInternal(redirectUrl);

      trackEvent('chat.redirected', { url: redirectUrl, isInternal });
      informHostingParentOnRedirect(redirectUrl);

      if (isInternal) {
        navigate(getUrlPath(redirectUrl));
      } else {
        fallback(redirectUrl);
      }
    },
    [webRedirectFn, navigate, trackEvent],
  );

  const nativeActionFn = useCallback(
    (redirectUrl: string, fallback: (url: string) => void = DEFAULT_NATIVE_REDIRECT_ACTION): void => {
      const [url] = redirectUrl.split('?');
      const redirectOptions =
        chatActionMap(redirectUrl, nav) ?? navActionMap(url, nav, auth) ?? regexActionMap(redirectUrl);

      if (redirectOptions == null) {
        webActionFn(redirectUrl, fallback);

        return;
      }

      trackEvent('chat.redirected', {
        url: redirectUrl,
        isNative: true,
        nativeActionType: redirectOptions.actionType,
        nativeActionParams: redirectOptions.actionParams,
      });

      void redirectOptions.action(redirectUrl);
    },
    [webActionFn, nav, auth, trackEvent],
  );

  const value = useMemo(
    () => ({
      redirectFn: isNativeApp() ? nativeActionFn : webActionFn,
      webRedirectFn: webActionFn,
      nativeRedirectFn: nativeActionFn,
      isUrlInternal,
      isUrlSameScript,
    }),
    [nativeActionFn, webActionFn],
  );

  return <RedirectContext.Provider value={value}>{children}</RedirectContext.Provider>;
};

export function useRedirectContext(): RedirectContextValue {
  return useContext(RedirectContext);
}
